import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { ReactSVG } from 'react-svg';
import * as XLSX from 'xlsx';
import { JSONTree } from 'react-json-tree';
import { renderAsync } from 'docx-preview';
import { useIntl } from 'react-intl';
import messages from '../messages';
import { documentStyles } from './utils';

import axios from 'axios';
import { saveAs } from 'file-saver';
import { Box, Button, CircularProgress, Collapse, Divider, IconButton, MenuItem, Pagination, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { ArrowBack, Download, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { getStyles } from './styles';
import './styles.css';
import LoadBar from '../../../../utils/LoadBar';
import { tostAlert } from '../../../../utils/AlertToast';
import { apiErrorHandler } from '../../../../utils/ApiErrorHandler';
import { IconSvg } from '../../../../utils/globalIcons';
import TemplateExtractionApi from '../../../../Api/Services/AppflyteBackend/TemplateExtractionApi';
import { fetchSubscriberId, fetchSubscriptionId, fetchThirdPartyToken, fetchThirdPartyTokenProvider } from '../../../../utils/GetAccountDetails';
import FilesApi from '../../../../Api/Services/AppflyteBackend/FileServiceApi';
import { addProgrammaticAnnotation, generateSearchKeysNew, performOCR } from './utils';
import { useAppContext } from '../../../../context/AppContext';
import handleFileUpload from '../../../../utils/fileUpload';
import { ANALYTICS_S3_BUCKET_FOLDER, ANALYTICS_S3_BUCKET_SCRIPT_FILE } from '../../../../utils/constants';

let s3_folder_name = ANALYTICS_S3_BUCKET_FOLDER
let s3_file_name = ANALYTICS_S3_BUCKET_SCRIPT_FILE
let bucket_name = window.__ENV__.S3_BUCKET_NAME;

const ViewDocument = () => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const { selectedProject, selectedOrganization,
    handleExtractionSteps, setReviewStatus, initialAuthData, updateAuthData } = useAppContext();

  const canvasRef = useRef(null);
  const contentRef = useRef(null);
  const relativeRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);

  const fileId = location.state?.file_id ?? null;
  const fileUrlPath = location.state?.file_path ?? null;
  const taskId = location.state?.task_id ?? null;
  const selectedFileName = location.state?.file_name ?? 'Unknown';
  const approvalStatus = location.state?.approval_status;

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [extractionResponse, setExtractionResponse] = useState({
    files: [{ mapped_fields: {}, tables: [], groups: [], other: {} }],
  });
  const [extractionResponseData, setExtractionResponseData] = useState({});
  const [groupOpenIndex, setGroupOpenIndex] = useState(null);
  const [tableOpenIndex, setTableOpenIndex] = useState(null);
  const [groupTableOpenIndex, setGroupTableOpenIndex] = useState(null);
  const [fieldEditKey, setFieldEditKey] = useState(null);
  const [fieldEditStatus, setFieldEditStatus] = useState(false);
  const [editTableData, setEditTableData] = useState({
    editFieldTableIndex: null,
    editFieldTableRowIndex: null,
    editFieldStatus: false,
    editFieldTableColumnKey: null,
  });
  const [editGroupData, setEditGroupData] = useState({
    editGroupIndex: null,
    editGroupFieldKey: null,
    editGroupFieldStatus: false,
    editGroupTableIndex: null,
    editGroupTableRowIndex: null,
    editGroupTableColumnKey: null,
    editGroupTableFieldStatus: false,
  });
  const [previewType, setPreviewType] = useState('file_preview');
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [annotations, setAnnotations] = useState({});
  const [fileType, setFileType] = useState(null);
  const [fileDoc, setFileDoc] = useState(null);
  const [filUrl, setFileUrl] = useState(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [pageTextItems, setPageTextItems] = useState([]);
  const [searchKeysData, setSearchKeysData] = useState([]);
  const [filePage, setFilePage] = useState(0);
  const [docxPages, setDocxPages] = useState([]);
  const [content, setContent] = useState('');
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [chatbotData, setChatbotData] = useState({ chatbotStatus: false, chatbotUrl: null, chatBotType: null });
  const visibleRows = 100;

  useEffect(() => {
    if (!fileId || !taskId || !fileUrlPath) {
      navigate('/all-documents');
    } else {
      updateAuthData(initialAuthData);
      fetchAllData();
    }
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
    //eslint-disable-next-line
  }, [fileId, taskId, fileUrlPath, navigate]);


  async function fetchFilePath(url) {
    const match = url.match(/https:\/\/[^.]+\.s3\.amazonaws\.com\/([^?]+)/);
    return match ? match[1] : null;
  }

  const fetchAllData = async () => {
    setDataLoading(true);

    const subscriber_id = await fetchSubscriberId()
    const subscription_id = await fetchSubscriptionId()
    const analytics_app_id = selectedProject?.payload?.analytics_app_id ?? null;
    const analytics_project_id = selectedProject?.payload?.analytics_project_id ?? null;
    const check_url = `${s3_folder_name}/${s3_file_name}/${subscriber_id}/${subscription_id}/${analytics_project_id}/${analytics_app_id}.js`;

    try {

      const [extractionResponse] = await Promise.all([
        getAllExtractionResult(),
        checkAnalyticsChatbot(check_url)
      ]);

      if (!window.pdfjsLib) {
        tostAlert('PDF.js is not loaded.', 'error');
        return;
      }

      const extractionData = extractionResponse?.data?.files?.[0] || extractionResponse?.extracted_data?.files?.[0] || {};
      const searchKeys = generateSearchKeysNew(extractionData);
      const files_data = extractionResponse?.data?.files || extractionResponse?.extracted_data?.files || [];
      const transformedFiles = files_data.map(file => ({
        mapped_fields: file.mapped_fields ?? {},
        tables: file.tables ?? [],
        groups: file.groups ?? [],
        other: file.other ?? {},
      }));
      setExtractionResponse({ files: transformedFiles });
      setExtractionResponseData(extractionResponse);
      setSearchKeysData(searchKeys);

      const match = fileUrlPath.match(/https:\/\/([^.]+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com/);
      const bucketName = match?.[1];

      let file_download_url = null;

      if (bucket_name !== bucketName) {
        file_download_url = fileUrlPath
      } else {
        const filePath = await fetchFilePath(fileUrlPath);
        const fileResponse = await FilesApi.getDownloadUrl(bucketName, filePath);
        if (fileResponse.status === 200) {
          const logo_res_data = fileResponse?.data?.at(-1) ?? null;
          file_download_url = Object.values(logo_res_data)?.at(-1) ?? null;
        }
      }

      if (file_download_url) {

        let fileExtension = selectedFileName.split('.').pop()?.toLowerCase();

        if (!fileExtension) {
          console.error('No file extension found for fileName:', selectedFileName);
          tostAlert(intl.formatMessage(messages.invalid_file), 'error');
          return;
        }

        if (!["docx", "xlsx", "pdf", "jpg", "jpeg", "png"].includes(fileExtension)) {
          return;
        }

        setFileType(fileExtension);
        setAnnotations({});
        setSelectedAnnotation(null);
        setFileUrl(file_download_url)

        if (fileExtension === 'docx' || fileExtension === 'xlsx') {
          setFileDoc({ uri: file_download_url, fileType: fileExtension });
          setTotalPages(1);
          setPageNumber(1);
        }

        if (fileExtension === "pdf") {
          const pdfDoc = await window.pdfjsLib.getDocument(file_download_url).promise;
          setTotalPages(pdfDoc.numPages);
          setPageNumber(1);
          setFileDoc(pdfDoc);
        }

        if (fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png') {
          setFileDoc({ uri: file_download_url, fileType: fileExtension });
          setTotalPages(1);
          setPageNumber(1);
        }
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (!chatbotData?.chatbotStatus || !chatbotData?.chatbotUrl) return;

    const scriptId = "extraction-chatbot-script";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = chatbotData.chatbotUrl;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      console.log("Cleanup — removing chatbot iframe + globals");

      try {
        window.AmeayaChatBot?.("destroy");
        if (window.AmeayaChatBot) delete window.AmeayaChatBot;
      } catch { }

      ["ameya-simple-chatbot-iframe", "ameya-chatbot-iframe"].forEach(id => {
        const iframe = document.getElementById(id);
        if (iframe) iframe.remove();
      });

      if (window.toggleChatbot) delete window.toggleChatbot;

      const oldScript = document.getElementById(scriptId);
      if (oldScript) oldScript.remove();
    };
  }, [chatbotData, location.pathname]);


  // -------------------------------------------------| pdf rendering |--------------------------------------------------
  const renderPDFPage = useCallback(async (pdf, pageNum) => {
    try {
      const page = await pdf.getPage(pageNum);
      const container = containerRef.current;
      if (!container) throw new Error('Container ref missing');

      const containerWidth = container.clientWidth;
      const defaultViewport = page.getViewport({ scale: 1 });
      const dynamicScale = Math.min(containerWidth / defaultViewport.width, 3);
      const viewport = page.getViewport({ scale: dynamicScale });

      const canvas = pdfCanvasRef.current;
      const annotationCanvas = canvasRef.current;
      if (!canvas || !annotationCanvas) throw new Error('Canvas refs missing');

      const devicePixelRatio = window.devicePixelRatio || 1;
      [canvas, annotationCanvas].forEach(c => {
        c.width = viewport.width * devicePixelRatio;
        c.height = viewport.height * devicePixelRatio;
        c.style.width = `${viewport.width}px`;
        c.style.height = `${viewport.height}px`;
      });

      container.style.height = `${viewport.height}px`;
      container.style.width = '';

      const context = canvas.getContext('2d');
      context.scale(devicePixelRatio, devicePixelRatio);
      const annotationContext = annotationCanvas.getContext('2d');
      annotationContext.scale(devicePixelRatio, devicePixelRatio);

      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      renderTaskRef.current = page.render({ canvasContext: context, viewport });
      await renderTaskRef.current.promise.catch(error => {
        if (error.name !== 'RenderingCancelledException') throw error;
      });
      renderTaskRef.current = null;

      let textItems = [];
      let isOcr = false;
      try {
        const textContent = await page.getTextContent();
        textItems = textContent.items
          .map(item => ({
            str: item.str.trim(),
            x: item.transform[4] * dynamicScale,
            y: viewport.height - (item.transform[5] * dynamicScale) - (item.height * dynamicScale),
            width: item.width * dynamicScale,
            height: (item.height || 12) * dynamicScale,
            fontName: item.fontName || 'unknown',
            dir: item.dir || 'ltr',
            originalY: item.transform[5],
          }))
          .filter(item => item.str);

        if (textItems.length === 0 || textItems.every(item => !item.str)) {
          setIsOcrProcessing(true);
          textItems = await performOCR(page, renderTaskRef, dynamicScale, viewport);
          isOcr = true;
        }
      } catch (error) {
        setIsOcrProcessing(true);
        textItems = await performOCR(page, renderTaskRef, dynamicScale, viewport).catch(ocrError => {
          console.warn('OCR failed:', ocrError);
          return [];
        });
        isOcr = true;
      } finally {
        setIsOcrProcessing(false);
      }
      setPageTextItems(textItems.map(item => ({ ...item, isOcr })));
      return textItems;
    } catch (error) {
      console.error('Error rendering PDF page:', error);
      tostAlert(intl.formatMessage(messages.pdf_render_error), 'error');
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (fileDoc && pageNumber && previewType === 'file_preview' && fileType === 'pdf') {
      renderPDFPage(fileDoc, pageNumber);
    }
    //eslint-disable-next-line
  }, [fileDoc, pageNumber, previewType, renderPDFPage]);

  // -----------------------------------------------------------------------------------------------------------------

  //  ----------------------------| word file |-------------------------------------------------------------------------
  function extractWordTextItems(container) {
    const textItems = [];
    const elements = container.querySelectorAll('p, span, td, li, h1, h2, h3, h4, h5, h6');

    const containerRect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;
    const scrollLeft = container.scrollLeft;
    const scale = 1;

    const measureWord = (word, style) => {
      const tempSpan = document.createElement('span');
      tempSpan.style.position = 'absolute';
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.fontFamily = style.fontFamily;
      tempSpan.style.fontWeight = style.fontWeight;
      tempSpan.style.fontStyle = style.fontStyle;
      tempSpan.style.fontSize = style.fontSize;
      tempSpan.textContent = word;

      document.body.appendChild(tempSpan);
      const wordRect = tempSpan.getBoundingClientRect();
      document.body.removeChild(tempSpan);

      return wordRect;
    };

    elements.forEach(element => {
      try {
        const fullText = element.textContent.trim();
        if (!fullText) return;

        const style = getComputedStyle(element);
        const elementRect = element.getBoundingClientRect();

        const words = fullText.split(/\s+/);
        let currentXOffset = 0;

        words.forEach(word => {
          if (!word) return;

          const wordRect = measureWord(word, style);

          const width = Math.ceil(wordRect.width / scale);
          const height = Math.ceil(wordRect.height / scale);
          const x = (elementRect.left + currentXOffset - containerRect.left + scrollLeft) / scale;
          const y = (elementRect.top - containerRect.top + scrollTop) / scale;

          if (width > 0 && height > 0 && x >= 0 && y >= 0) {
            textItems.push({
              str: word,
              x,
              y,
              width,
              height,
              fontName: style.fontFamily || 'Inter',
              fontWeight: style.fontWeight,
              fontStyle: style.fontStyle,
              fontSize: style.fontSize,
              color: style.color,
              dir: style.direction || 'ltr',
              domElement: element,
              element: element.tagName.toLowerCase(),
            });
          }
          currentXOffset += width + 2;
        });
      } catch (error) {
        console.warn('Error processing element', element.tagName, error);
      }
    });
    return textItems;
  }

  const splitDocxIntoPages = (wrapper) => {
    const sections = Array.from(wrapper.querySelectorAll('section.docx'));

    if (sections.length > 1) {
      return sections.map(section => section.cloneNode(true));
    }

    if (sections.length === 1) {
      const section = sections[0];
      const article = section.querySelector('article');

      if (!article) {
        return [section.cloneNode(true)];
      }

      // Get page dimensions from section styles
      const sectionStyle = window.getComputedStyle(section);
      const pageHeight = parseFloat(sectionStyle.minHeight) || 841.9; // Default A4 height in pt
      const paddingTop = parseFloat(sectionStyle.paddingTop) || 56.7;
      const paddingBottom = parseFloat(sectionStyle.paddingBottom) || 56.7;
      const contentHeight = pageHeight - paddingTop - paddingBottom;

      // Get all child elements from the article and measure their heights while in DOM
      const elements = Array.from(article.children);

      // Measure all element heights first while they're in the DOM
      const elementData = elements.map((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        const marginTop = parseFloat(style.marginTop) || 0;
        const marginBottom = parseFloat(style.marginBottom) || 0;
        const totalHeight = rect.height + marginTop + marginBottom;

        return {
          element: element,
          height: totalHeight
        };
      });

      // Split elements into pages based on cumulative height
      const pages = [];
      let currentPage = {
        section: document.createElement('section'),
        article: document.createElement('article'),
        height: 0
      };

      // Copy section attributes
      currentPage.section.className = section.className;
      currentPage.section.setAttribute('style', section.getAttribute('style'));

      elementData.forEach((item) => {
        // If adding this element would exceed page height, start a new page
        if (currentPage.height + item.height > contentHeight && currentPage.article.children.length > 0) {
          currentPage.section.appendChild(currentPage.article);
          pages.push(currentPage.section);

          // Start new page
          currentPage = {
            section: document.createElement('section'),
            article: document.createElement('article'),
            height: 0
          };
          currentPage.section.className = section.className;
          currentPage.section.setAttribute('style', section.getAttribute('style'));
        }

        currentPage.article.appendChild(item.element.cloneNode(true));
        currentPage.height += item.height;
      });

      // Add the last page
      if (currentPage.article.children.length > 0) {
        currentPage.section.appendChild(currentPage.article);
        pages.push(currentPage.section);
      }
      return pages.length > 0 ? pages : [section.cloneNode(true)];
    }

    return [wrapper.cloneNode(true)];
  };

  const loadWordDocument = async () => {
    try {
      const response = await fetch(fileDoc?.uri, { method: 'GET', mode: 'cors' });
      if (!response.ok) {
        throw new Error('Failed to fetch file: ' + response.statusText);
      }
      const fileBlob = await response.blob();
      if (fileBlob && contentRef.current && containerRef.current) {
        contentRef.current.innerHTML = '';

        // Render with breakPages: true to get page breaks
        await renderAsync(fileBlob, contentRef.current, null, {
          breakPages: true,
          ignoreHeight: false,
          ignoreWidth: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          experimental: true,
          className: 'docx-wrapper'
        });

        const wrapper = contentRef.current.querySelector('div.docx-wrapper');
        if (!wrapper) throw new Error('Document wrapper not found');

        // Split the document into pages
        const pages = splitDocxIntoPages(wrapper);

        setDocxPages(pages);
        setTotalPages(pages.length);
        setPageNumber(1);

        contentRef.current.innerHTML = '';
        if (pages.length > 0) {
          const pageClone = pages[0].cloneNode(true);

          // Scale the page to fit the container width only
          const containerWidth = containerRef.current.clientWidth;
          const pageWidth = parseFloat(window.getComputedStyle(pageClone).width) || 595.3;

          const scale = (containerWidth - 40) / pageWidth; // 40px for padding

          if (scale !== 1) {
            pageClone.style.transform = `scale(${scale})`;
            pageClone.style.transformOrigin = 'top left';
            pageClone.style.width = `${pageWidth}px`;

            // Create a wrapper to handle the scaled dimensions
            const wrapper = document.createElement('div');
            wrapper.style.width = `${pageWidth * scale}px`;
            wrapper.style.overflow = 'hidden';
            wrapper.appendChild(pageClone);
            contentRef.current.appendChild(wrapper);
          } else {
            contentRef.current.appendChild(pageClone);
          }

          const textItems = extractWordTextItems(contentRef.current);
          setPageTextItems(textItems);
        }
      }
    }
    catch (error) {
      console.log(error)
    }
  };

  useEffect(() => {
    if (fileType === 'docx' && fileDoc?.uri && previewType === 'file_preview') {
      loadWordDocument();
    }
    //eslint-disable-next-line
  }, [fileType, fileDoc, previewType]);

  useEffect(() => {
    if (fileType === 'docx' && docxPages.length > 0 && contentRef.current && containerRef.current && previewType === 'file_preview') {
      const pageIndex = pageNumber - 1;
      if (pageIndex >= 0 && pageIndex < docxPages.length) {
        contentRef.current.innerHTML = '';
        const pageClone = docxPages[pageIndex].cloneNode(true);

        // Scale the page to fit the container width only
        const containerWidth = containerRef.current.clientWidth;
        const pageWidth = parseFloat(window.getComputedStyle(pageClone).width) || 595.3;

        const scale = (containerWidth - 40) / pageWidth; // 40px for padding

        if (scale !== 1) {
          pageClone.style.transform = `scale(${scale})`;
          pageClone.style.transformOrigin = 'top left';
          pageClone.style.width = `${pageWidth}px`;

          // Create a wrapper to handle the scaled dimensions
          const wrapper = document.createElement('div');
          wrapper.style.width = `${pageWidth * scale}px`;
          wrapper.style.overflow = 'hidden';
          wrapper.appendChild(pageClone);
          contentRef.current.appendChild(wrapper);
        } else {
          contentRef.current.appendChild(pageClone);
        }

        const textItems = extractWordTextItems(contentRef.current);
        setPageTextItems(textItems);
      }
    }
  }, [pageNumber, fileType, docxPages, previewType]);

  //------------------------------------------------------------------------------------------------------------------

  // ----------------------------| Excel File |--------------------------------------
  const waitForContentRef = (timeout = 10000) => {
    return new Promise((resolve) => {
      if (contentRef.current && contentRef.current.innerHTML && contentRef.current.innerHTML.trim() !== '') {
        resolve(contentRef.current);
        return;
      }

      let checkCount = 0;
      const maxChecks = timeout / 100;

      const checkContentRef = () => {
        checkCount++;
        if (contentRef.current && contentRef.current.innerHTML && contentRef.current.innerHTML.trim() !== '') {
          resolve(contentRef.current);
          return;
        }
        if (checkCount >= maxChecks) {
          resolve(null);
          return;
        }
        setTimeout(checkContentRef, 100);
      };
      setTimeout(checkContentRef, 100);
    });
  };

  const extractTextFromExcel = useCallback(async (uri) => {
    if (fileType === "xlsx") {
      try {
        if (!uri) {
          console.error('Invalid URI:', uri);
          return [];
        }

        let contentElement = await waitForContentRef();
        if (!contentElement) {
          console.warn('contentRef not available');
          return [];
        }

        const textItems = [];
        const container = containerRef.current;
        if (!container) {
          console.warn('containerRef not set');
          return [];
        }

        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const scrollLeft = container.scrollLeft;
        const scale = 1;

        const selectors = fileType === 'xlsx' ? 'td:not(.merged)' : 'p, td, h1, h2, h3, h4, h5, h6, li, span, div';
        const renderedElements = contentElement.querySelectorAll(selectors);

        if (renderedElements.length === 0) {
          console.warn('No rendered elements found');
          return [];
        }

        renderedElements.forEach((element) => {
          const text = element.textContent?.trim();
          if (text) {
            try {
              const rect = element.getBoundingClientRect();
              const x = (rect.left - containerRect.left + scrollLeft) / scale;
              const y = (rect.top - containerRect.top + scrollTop) / scale;
              const width = rect.width / scale;
              const height = rect.height / scale;

              if (width > 0 && height > 0 && x >= 0 && y >= 0) {
                const style = getComputedStyle(element);
                textItems.push({
                  str: text,
                  x,
                  y,
                  width,
                  height,
                  fontName: style.fontFamily || 'Calibri',
                  fontWeight: style.fontWeight,
                  fontStyle: style.fontStyle,
                  fontSize: style.fontSize,
                  color: style.color,
                  dir: style.direction || 'ltr',
                  originalY: y,
                  element: element.tagName.toLowerCase(),
                  domElement: element
                });
              }
            } catch (error) {
              console.warn('Error processing element', element.tagName, error);
            }
          }
        });

        return textItems;
      } catch (error) {
        console.error('Error extracting text from non-PDF:', error);
        return [];
      }
    }
  }, [fileType]);

  useEffect(() => {
    if (content && fileType === 'xlsx' && fileDoc?.uri) {
      const attemptExtract = async (attempts = 5, delay = 1000) => {
        await new Promise(resolve => setTimeout(resolve, 500));
        for (let i = 0; i < attempts; i++) {
          const textItems = await extractTextFromExcel(fileDoc?.uri);
          if (textItems.length > 0) {
            setPageTextItems(textItems);
            return;
          }
          if (i < attempts - 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
        setPageTextItems([]);
      };
      attemptExtract();
    }
  }, [content, fileType, fileDoc, extractTextFromExcel]);

  const parseExcelFile = async (uri, selectedSheetName) => {
    try {
      const response = await fetch(uri, { method: 'GET', mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Failed to fetch Excel file: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });
      const sheetName = selectedSheetName || workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false }).slice(0, visibleRows);
      const merges = worksheet['!merges'] || [];
      const colWidths = worksheet['!cols'] || [];
      const rowHeights = worksheet['!rows'] || [];

      setSheetNames(workbook.SheetNames);
      if (!selectedSheetName) setSelectedSheet(workbook.SheetNames[0]);

      const maxCols = jsonData.reduce((max, row) => Math.max(max, row.length), 0);
      const columnLetters = Array.from({ length: maxCols }, (_, i) => XLSX.utils.encode_col(i));

      const htmlContent = `
                    <div class="excel-table-wrapper">
                        <table class="document-content xlsx">
                            <thead>
                                <tr>
                                    <th class="corner" style="width: 50px;"></th> 
                                        ${columnLetters.map((letter, colIndex) =>
        `<th class="column-header" style="width: ${colWidths[colIndex]?.wpx || 100}px;">${letter}</th>`).join('')}
                                </tr>
                            </thead>
                            <tbody>
                                ${jsonData.map((row, rowIndex) => {
          const rowHeight = rowHeights[rowIndex]?.hpx || 24;
          return `
                                         <tr style="height: ${rowHeight}px;">
                                            <th class="row-header" style="width: 50px;">${rowIndex + 1}</th>
                                            ${columnLetters.map((_, colIndex) => {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
            const cellObj = worksheet[cellRef] || {};
            const cellValue = row[colIndex] || '';
            const cellStyle = cellObj.s || {};
            const width = colWidths[colIndex]?.wpx || 100;
            const isMerged = merges.find(merge =>
              rowIndex >= merge.s.r && rowIndex <= merge.e.r &&
              colIndex >= merge.s.c && colIndex <= merge.e.c
            );
            const style = {
              fontWeight: cellStyle.font?.bold ? 'bold' : 'normal',
              fontStyle: cellStyle.font?.italic ? 'italic' : 'normal',
              fontSize: cellStyle.font?.sz ? `${cellStyle.font.sz}pt` : '10pt',
              color: cellStyle.font?.color?.rgb ? `#${cellStyle.font.color.rgb.slice(2)}` : '#000000',
              backgroundColor: cellStyle.fill?.fgColor?.rgb ? `#${cellStyle.fill.fgColor.rgb.slice(2)}` : '#ffffff',
              textAlign: cellStyle.alignment?.horizontal || 'left',
              verticalAlign: cellStyle.alignment?.vertical || 'middle',
              width: `${width}px`,
              minWidth: `${width}px`,
              height: `${rowHeight}px`
            };
            const mergeAttrs = isMerged ? `colspan="${isMerged.e.c - isMerged.s.c + 1}" rowspan="${isMerged.e.r - isMerged.s.r + 1}" class="merged"` : '';
            if (isMerged && (rowIndex !== isMerged.s.r || colIndex !== isMerged.s.c)) return '';
            const styleStr = Object.entries(style).map(([k, v]) => `${k}: ${v}`).join(';');
            return `<td ${mergeAttrs} style="${styleStr}">${cellValue}</td>`;
          }).join('')}
                                        </tr>`;
        }).join('')}
                            </tbody>
                        </table>
                    </div>
                `;

      return { htmlContent, jsonData };
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      throw error;
    }
  };

  const loadExcelDocument = async () => {
    setLoading(true);
    try {
      const response = await fetch(fileDoc?.uri, { method: 'GET', mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      const { htmlContent } = await parseExcelFile(fileDoc?.uri, selectedSheet);
      setContent(htmlContent);
      setTotalPages(1);
      setPageNumber(1);
    } catch (error) {
      console.error('Error rendering excel document:', error);
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fileType === 'xlsx' && fileDoc?.uri && containerRef.current) {
      loadExcelDocument();
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (canvas && container) {
        const updateCanvasSize = () => {
          const containerRect = container.getBoundingClientRect();
          const devicePixelRatio = window.devicePixelRatio || 1;
          canvas.width = containerRect.width * devicePixelRatio;
          canvas.height = containerRect.height * devicePixelRatio;
          canvas.style.width = `${containerRect.width}px`;
          canvas.style.height = `${containerRect.height}px`;
          const ctx = canvas.getContext('2d');
          ctx.scale(devicePixelRatio, devicePixelRatio);
        };
        updateCanvasSize();
        const resizeObserver = new ResizeObserver(updateCanvasSize);
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
      }
    }
    //eslint-disable-next-line
  }, [fileType, fileDoc, selectedSheet]);

  const renderExcelContent = () => (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <style dangerouslySetInnerHTML={{ __html: documentStyles }} />
      <Box
        ref={contentRef}
        className="document-content xlsx"
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #d9d9d9',
          backgroundColor: '#f9f9f9',
          height: 'calc(100% - 40px)',
        }}
      >
        <div
          style={{
            transformOrigin: 'top left',
            width: '100%',
            height: '100%',
          }}
          dangerouslySetInnerHTML={{ __html: content || '' }}
          data-testid="document-content"
        />
      </Box>

      <div className="excel-tabs">
        {sheetNames.map((sheet, index) => (
          <div
            key={index}
            className={`excel-tab ${sheet === selectedSheet ? 'active' : ''}`}
            onClick={async () => {
              setSelectedSheet(sheet);
              const { htmlContent } = await parseExcelFile(fileDoc.uri, sheet);
              setContent(htmlContent);
            }}
          >
            {sheet}
          </div>
        ))}
        <div style={{ flex: 1, borderBottom: '1px solid #c7c7c7' }} />
      </div>
    </Box>
  );

  const renderContent = () => {
    if (fileType === 'xlsx') {
      return renderExcelContent();
    }
    return null;
  };

  // -----------------------------------------------------

  // ------------------------------------------| markign and anotation for all files |----------------------------------
  const findKeywordCoordinates = useCallback(async (keywordList = [], options = {}) => {
    const {
      color = '#440ec3ff',
      padding = 4,
      caseSensitive = false,
      maxDistance = 20,
      maxXDistance = 20,
      maxBoxWidth = 150,
      maxBoxHeight = 100,
      fuzzyMatchThreshold = 0.9,
      onAnnotationAdded = () => { },
    } = options;

    try {
      const annotationsData = [];
      const notFoundKeywords = new Set(keywordList);
      const normalizeText = text => (caseSensitive ? text : text.toLowerCase().replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim());

      const wordPositions = pageTextItems.map((item, index) => ({
        text: normalizeText(item.str),
        originalText: item.str,
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height || 12,
        index,
        confidence: item.confidence || 100,
      }));

      const calculateBoundingBox = (positions, keyword) => {
        const xStart = Math.min(...positions.map(p => p.x));
        const xEnd = Math.max(...positions.map(p => p.x + p.width));
        const yStart = Math.min(...positions.map(p => p.y));
        const yEnd = Math.max(...positions.map(p => p.y + p.height));
        const width = xEnd - xStart;
        const height = yEnd - yStart;

        if (width > maxBoxWidth || height > maxBoxHeight) {
          console.warn(`Bounding box for "${keyword}" too large: width=${width}, height=${height}`);
          return null;
        }

        return [
          { x: xStart - padding, y: yStart - padding },
          { x: xEnd + padding, y: yStart - padding },
          { x: xEnd + padding, y: yEnd + padding },
          { x: xStart - padding, y: yEnd + padding },
        ];
      };

      const isSimilar = (text1, text2) => {
        const maxLen = Math.max(text1.length, text2.length);
        if (maxLen === 0) return 1.0;
        const distance = Array(text2.length + 1)
          .fill(null)
          .map(() => Array(text1.length + 1).fill(null));

        for (let i = 0; i <= text1.length; i++) distance[0][i] = i;
        for (let j = 0; j <= text2.length; j++) distance[j][0] = j;

        for (let j = 1; j <= text2.length; j++) {
          for (let i = 1; i <= text1.length; i++) {
            const indicator = text1[i - 1] === text2[j - 1] ? 0 : 1;
            distance[j][i] = Math.min(
              distance[j][i - 1] + 1,
              distance[j - 1][i] + 1,
              distance[j - 1][i - 1] + indicator
            );
          }
        }
        return 1 - distance[text2.length][text1.length] / maxLen;
      };

      const searchKeyword = (keyword, isMultiWord = false) => {
        const matches = [];
        const searchText = normalizeText(keyword);
        const isSpecialCharKeyword = keyword.includes('/') || keyword.includes('-');
        const escapedSearchText = searchText.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
        // Anchor with word boundaries where the term starts/ends with a word character so short
        // tokens match as standalone words ("CFU", "g") instead of as substrings of unrelated
        // words ("Beige", "refined"). Symbol-edged terms ("> 99.5") keep the looser match.
        const boundaryStart = /^\w/.test(searchText) ? '\\b' : '';
        const boundaryEnd = /\w$/.test(searchText) ? '\\b' : '';
        const fullRegex = new RegExp(`${boundaryStart}${escapedSearchText}${boundaryEnd}`, caseSensitive ? '' : 'i');

        for (const item of wordPositions) {
          const isExactMatch = fullRegex.test(item.text);
          const isFuzzyMatch = item.confidence < 95 && isSimilar(item.text, searchText) >= fuzzyMatchThreshold;
          if (isExactMatch || isFuzzyMatch) {
            const coordinates = calculateBoundingBox([item], keyword);
            if (coordinates) {
              matches.push({
                text: caseSensitive ? item.originalText : keyword,
                label: 'KEYWORD',
                color,
                coordinates,
                indices: [item.index],
              });
            }
          }
        }

        if (isMultiWord && !isSpecialCharKeyword && matches.length === 0) {
          const parts = searchText.split(/\s+/).filter(part => part);
          for (let i = 0; i < wordPositions.length; i++) {
            let partIndex = 0;
            const matchedPositions = [];
            let j = i;

            while (partIndex < parts.length && j < wordPositions.length) {
              const item = wordPositions[j];
              const isAligned =
                !matchedPositions.length ||
                (Math.abs(item.y - matchedPositions[0].y) < maxDistance &&
                  Math.abs(
                    (item.x + (matchedPositions.length > 0 ? item.width : 0)) -
                    (matchedPositions[matchedPositions.length - 1].x + matchedPositions[matchedPositions.length - 1].width)
                  ) < maxXDistance);

              const itemWords = item.text.split(/\s+/);
              let partsMatched = 0;
              for (let k = partIndex; k < parts.length && k - partIndex < itemWords.length; k++) {
                const partRegex = new RegExp(parts[k].replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), caseSensitive ? '' : 'i');
                const isPartFuzzyMatch =
                  item.confidence < 95 && isSimilar(itemWords[k - partIndex], parts[k]) >= fuzzyMatchThreshold;
                if (partRegex.test(itemWords[k - partIndex]) || isPartFuzzyMatch) {
                  partsMatched++;
                } else {
                  break;
                }
              }

              if (partsMatched > 0) {
                matchedPositions.push(item);
                partIndex += partsMatched;
              } else if (isAligned) {
                const partRegex = new RegExp(parts[partIndex].replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), caseSensitive ? '' : 'i');
                const isPartFuzzyMatch = item.confidence < 95 && isSimilar(item.text, parts[partIndex]) >= fuzzyMatchThreshold;
                if (partRegex.test(item.text) || isPartFuzzyMatch) {
                  matchedPositions.push(item);
                  partIndex++;
                }
              }

              if (partIndex < parts.length && !partsMatched) {
                for (let k = Math.max(0, i - 2); k < i; k++) {
                  const prevItem = wordPositions[k];
                  const prevAligned =
                    Math.abs(prevItem.y - (matchedPositions[0]?.y || item.y)) < maxDistance &&
                    Math.abs((prevItem.x + prevItem.width) - (matchedPositions[matchedPositions.length - 1]?.x || item.x)) <
                    maxXDistance;
                  const partRegex = new RegExp(parts[partIndex].replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), caseSensitive ? '' : 'i');
                  const isPartFuzzyMatch = prevItem.confidence < 95 && isSimilar(prevItem.text, parts[partIndex]) >= fuzzyMatchThreshold;
                  if (prevAligned && (partRegex.test(prevItem.text) || isPartFuzzyMatch)) {
                    matchedPositions.push(prevItem);
                    partIndex++;
                    break;
                  }
                }
              }

              j++;
              if (partIndex === parts.length) {
                const coordinates = calculateBoundingBox(matchedPositions, keyword);
                if (coordinates) {
                  matches.push({
                    text: keyword,
                    label: 'KEYWORD',
                    color,
                    coordinates,
                    indices: matchedPositions.map(p => p.index).sort((a, b) => a - b),
                  });
                }
                i = j - 1;
                break;
              }
            }
          }
        }
        return matches;
      };

      const matchedKeywords = new Set();
      const correctedKeywordList = keywordList.map(keyword => (keyword.toLowerCase() === 'due dale' ? 'Due date' : keyword));

      for (const keyword of correctedKeywordList) {
        if (matchedKeywords.has(keyword)) continue;
        const isMultiWord = keyword.includes(' ') && !keyword.includes('/') && !keyword.includes('-');
        const matches = searchKeyword(keyword, isMultiWord);

        if (matches.length > 0) {
          notFoundKeywords.delete(keyword);
          matchedKeywords.add(keyword);
          for (const match of matches) {
            const annotation = await addProgrammaticAnnotation(match, match.coordinates, pageNumber);
            annotationsData.push(annotation);
            onAnnotationAdded(annotation);
          }
        }
      }

      setAnnotations(prev => ({
        ...prev,
        [pageNumber]: [...(prev[pageNumber] || []).filter(ann => ann.label !== 'KEYWORD'), ...annotationsData],
      }));

      return { annotationsData, notFoundKeywords: Array.from(notFoundKeywords) };
    } catch (error) {
      return { annotationsData: [], notFoundKeywords: keywordList };
    }
  }, [pageTextItems, pageNumber]);

  const defaultTextSearch = useCallback(async () => {
    if (searchKeysData.length > 0 && fileDoc) {
      await findKeywordCoordinates(searchKeysData);
    } else {
      await findKeywordCoordinates([]);
    }
  }, [searchKeysData, fileDoc, findKeywordCoordinates]);

  useEffect(() => {
    defaultTextSearch();
  }, [defaultTextSearch, pageTextItems]);

  const drawPolygon = useCallback((ctx, polygon) => {
    if (polygon.length < 2) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(polygon[0].x, polygon[0].y);
    for (let i = 1; i < polygon.length; i++) {
      ctx.lineTo(polygon[i].x, polygon[i].y);
    }
    if (polygon.length > 2) ctx.closePath();
    ctx.fillStyle = 'rgba(146, 229, 161, 0.5)';
    ctx.fill();
    ctx.restore();
  }, []);

  const redrawAnnotations = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const currentPageAnnotations = annotations[pageNumber] || [];
    currentPageAnnotations.forEach(annotation => {
      drawPolygon(ctx, annotation.polygon, annotation.color, selectedAnnotation?.id === annotation.id);
    });
  }, [annotations, pageNumber, selectedAnnotation, drawPolygon]);

  useEffect(() => {
    if (fileType === 'xlsx') {
      const canvas = canvasRef.current;
      const container = containerRef.current;

      if (canvas && container) {
        const updateCanvasSize = () => {
          const containerRect = container.getBoundingClientRect();
          const devicePixelRatio = window.devicePixelRatio || 1;
          canvas.width = containerRect.width * devicePixelRatio;
          canvas.height = containerRect.height * devicePixelRatio;
          canvas.style.width = `${containerRect.width}px`;
          canvas.style.height = `${containerRect.height}px`;
          const ctx = canvas.getContext('2d');
          ctx.scale(devicePixelRatio, devicePixelRatio);
          redrawAnnotations();
        };

        updateCanvasSize();
        const resizeObserver = new ResizeObserver(updateCanvasSize);
        resizeObserver.observe(container);

        const handleScroll = () => {
          updateCanvasSize();
        };
        container.addEventListener('scroll', handleScroll);

        return () => {
          resizeObserver.disconnect();
          container.removeEventListener('scroll', handleScroll);
        };
      }
    } else {
      redrawAnnotations();
    }
    //eslint-disable-next-line
  }, [redrawAnnotations]);


  const changePage = useCallback(
    async newPageNumber => {
      if (!fileDoc || newPageNumber < 1 || newPageNumber > totalPages) return;
      setPageNumber(newPageNumber);
      setSelectedAnnotation(null);
      if (fileType === 'pdf') {
        await renderPDFPage(fileDoc, newPageNumber);
      }
      // For DOCX, the page change is handled by the useEffect hook
    },
    [fileDoc, totalPages, renderPDFPage, fileType]
  );
  // ------------------------------------------------------------------------------------------------------------------


  const getAllExtractionResult = async () => {
    try {
      const subscriberId = await fetchSubscriberId();
      const subscriptionId = await fetchSubscriptionId();
      const organizationId = selectedOrganization?.payload?.__auto_id__;
      const projectId = selectedProject?.payload?.__auto_id__;

      const filePath =
        approvalStatus === 'PENDING'
          ? `ameya-extraction/subscriber/${subscriberId}/subscription_id/${subscriptionId}/organization_id/${organizationId}/project_id/${projectId}/extracted/${fileId}.json`
          : `ameya-extraction/subscriber/${subscriberId}/subscription_id/${subscriptionId}/organization_id/${organizationId}/project_id/${projectId}/reviewed/${fileId}.json`;

      const response = await FilesApi.getDownloadUrl(bucket_name, filePath);
      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        const responseData = response.data.at(-1) ?? {};
        const fileUrl = Object.values(responseData)?.at(-1) ?? null;
        const fileResponse = await axios.get(fileUrl);
        return fileResponse.data ?? {};
      }
      console.warn('Unexpected response format or empty data');
      return {};
    } catch (error) {
      console.error('Error in getAllExtractionResult:', error);
      return {};
    }
  };

  const checkAnalyticsChatbot = async (check_url) => {
    const response = await FilesApi.getDownloadUrl(bucket_name, check_url);
    if (response?.status !== 200) {
      return
    }

    const logo_res_data = response?.data?.at(-1) ?? null;
    const download_url = Object.values(logo_res_data ?? {})?.at(-1) ?? null;

    if (!download_url) {
      return
    }

    try {
      const response = await fetch(download_url, { method: 'GET' });
      const scriptText = await response.text();
      const configMatch = scriptText.match(/const\s+chatbotConfig\s*=\s*(\{[\s\S]*?\})\s*;?\s*\/\/\s*END\s+chatbotConfig/);
      if (configMatch && configMatch[1]) {
        const chatbotConfig = JSON.parse(configMatch[1]) ?? {};
        const app_bot_type = chatbotConfig?.APP_DETAILS?.app_bot_type ?? '';
        setChatbotData(prev => ({
          ...prev,
          chatbotStatus: true,
          chatbotUrl: download_url,
          chatBotType: app_bot_type
        }))

      }
      return
    } catch {
      return
    }

  };

  const handleFieldValueChange = (newValue, fileIndex, fieldKey) => {
    setExtractionResponse(prev => ({
      ...prev,
      files: prev.files.map((file, index) =>
        index === fileIndex
          ? { ...file, mapped_fields: { ...file.mapped_fields, [fieldKey]: { ...file.mapped_fields[fieldKey], value: newValue } } }
          : file
      ),
    }));
  };

  const handleTableValueChange = (fileIndex, tableIndex, rowIndex, columnKey, newValue) => {
    setExtractionResponse(prev => {
      const updatedFiles = [...prev.files];
      const updatedTables = [...updatedFiles[fileIndex].tables];
      const updatedRows = [...updatedTables[tableIndex].rows];

      const updatedRow = { ...updatedRows[rowIndex] };
      const finalValue = newValue.trim() === "" ? "-" : newValue;

      updatedRow[columnKey] = finalValue;

      updatedRows[rowIndex] = updatedRow;
      updatedTables[tableIndex] = {
        ...updatedTables[tableIndex],
        rows: updatedRows
      };
      updatedFiles[fileIndex] = {
        ...updatedFiles[fileIndex],
        tables: updatedTables
      };

      return { ...prev, files: updatedFiles };
    });
  };

  const handleGroupFieldValueChange = (fileIndex, groupIndex, fieldKey, newValue) => {
    setExtractionResponse(prev => {
      const updatedFiles = [...prev.files];
      const updatedGroups = updatedFiles[fileIndex].groups.map((group, gIndex) =>
        gIndex === groupIndex && group.fields?.[fieldKey]
          ? { ...group, fields: { ...group.fields, [fieldKey]: { ...group.fields[fieldKey], value: newValue } } }
          : group
      );
      updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], groups: updatedGroups };
      return { ...prev, files: updatedFiles };
    });
  };

  const handleGroupTableFieldValueChange = (fileIndex, groupIndex, tableIndex, rowIndex, columnKey, newValue) => {
    setExtractionResponse(prev => {
      const updatedFiles = [...prev.files];

      updatedFiles[fileIndex].groups = updatedFiles[fileIndex].groups.map(
        (group, gIndex) => {
          if (gIndex !== groupIndex || !group.tables?.[tableIndex]) return group;

          const updatedTables = [...group.tables];
          const table = updatedTables[tableIndex];

          const updatedRows = table.rows.map((row, rIndex) => {
            if (rIndex !== rowIndex) return row;

            const updatedRow = { ...row };
            const finalValue = newValue.trim() === "" ? "-" : newValue;
            updatedRow[columnKey] = finalValue;

            return updatedRow;
          });

          updatedTables[tableIndex] = { ...table, rows: updatedRows };

          return { ...group, tables: updatedTables };
        }
      );

      return { ...prev, files: updatedFiles };
    });
  };

  const updateExtractionData = async () => {
    try {
      const clonedData = JSON.parse(JSON.stringify(extractionResponse));
      const fileList = clonedData?.files ?? [];
      if (!fileList.length) {
        tostAlert(intl.formatMessage(messages.no_file_data), 'error');
        return false;
      }

      const updatedFiles = fileList.map(({ other, ...rest }) => ({ ...rest, ...(other ?? {}) }));
      const { files, ...otherExtractionFields } = extractionResponseData;
      const finalData = { ...clonedData, files: updatedFiles, ...otherExtractionFields };

      const subscriberId = await fetchSubscriberId();
      const subscriptionId = await fetchSubscriptionId();
      const organizationId = selectedOrganization?.payload?.__auto_id__;
      const projectId = selectedProject?.payload?.__auto_id__;

      const filename = `ameya-extraction/subscriber/${subscriberId}/subscription_id/${subscriptionId}/organization_id/${organizationId}/project_id/${projectId}/reviewed/${fileId}.json`;
      const blob = new Blob([JSON.stringify(finalData)], { type: 'application/json' });
      const file = new File([blob], filename, { type: 'application/json' });
      const response = await handleFileUpload(filename, file);
      return response.status === 200;
    } catch (error) {
      console.error('Error updating extraction data:', error);
      return false;
    }
  };

  // const updateStatus = async () => {
  //   try {
  //     const reqObj = { task_id: taskId, file_id: fileId, approve: true };
  //     const response = await TemplateExtractionApi.updateExtractionTask(JSON.stringify(reqObj), selectedFileName);
  //     return response.status === 200;
  //   } catch (error) {
  //     console.error('Error updating status:', error);
  //     return false;
  //   }
  // };

  const updateStatus = async () => {
    try {
      const updatekey = null;
      const reqObj = {
        "id": taskId,
        "fields": [{
          "path": "$.approval_status",
          "value": "APPROVED"
        }]
      }
      const response = await TemplateExtractionApi.updateTask(JSON.stringify(reqObj), taskId, updatekey);
      return response.status === 200;
    } catch (error) {
      console.error('Error updating status:', error);
      return false;
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const hasData = extractionResponse?.files?.some(
        file =>
          (file.tables?.length ?? 0) > 0 ||
          (file.groups?.length ?? 0) > 0 ||
          Object.keys(file.mapped_fields ?? {}).length > 0
      );
      if (!hasData) {
        tostAlert(intl.formatMessage(messages.approve_data_error), 'error');
        return;
      }

      const uploadSuccess = await updateExtractionData();
      if (!uploadSuccess) {
        tostAlert(intl.formatMessage(messages.invalid_file), 'error');
        return;
      }

      const updateSuccess = await updateStatus();
      if (updateSuccess) {
        setReviewStatus(true)
        navigate('/all-documents');
        handleExtractionSteps('is_review_extracted_data_done')
        tostAlert(intl.formatMessage(messages.approve_success), 'success');
      } else {
        tostAlert(intl.formatMessage(messages.approve_error), 'error');
      }
    } catch (error) {
      console.error('Error approving document:', error);
      apiErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const formatLabel = label => {
    if (typeof label !== 'string') return '';
    return label
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const lightTheme = {
    base00: '#ffffff',
    base01: '#f0f0f0',
    base02: '#e0e0e0',
    base03: '#d0d0d0',
    base04: '#999999',
    base05: '#333333',
    base06: '#222222',
    base07: '#000000',
    base08: '#ff0000',
    base09: '#ff9900',
    base0A: '#ffcc00',
    base0B: '#33cc33',
    base0C: '#00cccc',
    base0D: '#0066ff',
    base0E: '#cc66cc',
    base0F: '#990000',
  };

  const handleDownloadFile = async () => {
    try {
      const response = await fetch(filUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      saveAs(blob, selectedFileName);
      setDownloadAnchorEl(null);
    } catch (error) {
      console.error('Error downloading file:', error);
      tostAlert(intl.formatMessage(messages.download_error), 'error');
    }
  };

  const handleDownloadJSON = () => {
    const clonedData = JSON.parse(JSON.stringify(extractionResponse));
    const fileList = clonedData?.files ?? [];
    if (
      extractionResponse?.files?.every(
        file =>
          (file.tables?.length ?? 0) === 0 &&
          (file.groups?.length ?? 0) === 0 &&
          Object.keys(file.mapped_fields ?? {}).length === 0
      )
    ) {
      tostAlert(intl.formatMessage(messages.no_file_data), 'error');
      return;
    }

    const updatedFiles = fileList.map(({ other, ...rest }) => ({ ...rest, ...(other ?? {}) }));
    const { files, ...otherExtractionFields } = extractionResponseData;
    const finalData = { ...clonedData, files: updatedFiles, ...otherExtractionFields };

    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(finalData, null, 2))}`;
    const link = document.createElement('a');
    link.href = jsonString;
    link.download = 'extraction_data.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadAnchorEl(null);
  };

  const isLongName = selectedFileName?.length > 20;

  const getDecodedName = name => {
    try {
      return decodeURIComponent(name.replace(/\+/g, ' '));
    } catch {
      return name;
    }
  };

  const GroupDivider = () => {
    // Only render as a separator when there is content (fields or tables) above the groups section.
    const hasContentAbove = filteredFields.length > 0 || hasTableRows;
    if (!hasContentAbove) return null;

    const groups = extractionResponse?.files?.[filePage]?.groups || [];
    if (groups.length === 0) return null;
    const hasValidGroups = groups.some(group => {
      const hasFieldValues = Object.values(group?.fields ?? {}).some(
        field => String(field?.value ?? '').trim() !== ''
      );
      const hasTableValues = (group?.tables ?? []).some(table =>
        table?.rows?.some(row => String(row?.result ?? '').trim() !== '')
      );
      return hasFieldValues || hasTableValues;
    });
    return hasValidGroups ? <Divider sx={{ margin: '10px 0', borderColor: '#E5E7EB' }} /> : null;
  };

  const FieldItem = ({ field, keyName, index }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
        {field?.source_label}
      </Typography>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', marginLeft: '5px' }}>
        {fieldEditStatus && fieldEditKey === keyName && approvalStatus === 'PENDING' ? (
          <TextField
            value={field?.value ?? ''}
            size="small"
            autoFocus
            onChange={e => handleFieldValueChange(e.target.value, filePage, keyName)}
            onBlur={() => setFieldEditStatus(false)}
          />
        ) : (
          <Typography
            sx={{ fontSize: '14px', fontWeight: 400, textAlign: 'right', cursor: 'pointer', wordBreak: 'break-word', whiteSpace: 'normal' }}
            onClick={() => {
              setFieldEditStatus(true);
              setFieldEditKey(keyName);
            }}
          >
            {String(field?.value ?? '').trim() || '--'}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const TableItem = ({ table, index }) => {
    const columns = table?.rows?.length ? Object.keys(table.rows[0]).filter(key => key !== "_unmapped_columns") : [];
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
            {formatLabel(table?.source_label)}
          </Typography>

          <IconButton
            sx={{ fontSize: '15px', fontWeight: 500 }}
            onClick={() => setTableOpenIndex(tableOpenIndex === index ? null : index)}
          >
            {tableOpenIndex === index ? (
              <KeyboardArrowUp sx={{ color: '#0B51C5' }} />
            ) : (
              <KeyboardArrowDown sx={{ color: '#0B51C5' }} />
            )}
          </IconButton>
        </Box>

        <Collapse in={tableOpenIndex === index} timeout="auto" unmountOnExit sx={{ backgroundColor: '#FFFFFF' }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "Arial, sans-serif",
              border: "1px solid #ccc"
            }}
          >
            <thead>
              <tr>
                {columns.map((col, colIndex) => (
                  <th
                    key={colIndex}
                    style={{
                      textAlign: "left",
                      fontSize: "14px",
                      fontWeight: 500,
                      padding: "8px",
                      borderBottom: "1px solid #ccc",
                      borderRight: colIndex !== columns.length - 1 ? "1px solid #ccc" : "none",
                      textTransform: "capitalize"
                    }}
                  >
                    {col.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {table?.rows?.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((col, colIndex) => (
                    <td
                      key={colIndex}
                      style={{
                        textAlign: colIndex === columns.length - 1 ? "right" : "left",
                        fontSize: "14px",
                        padding: "8px",
                        borderTop: "1px solid #eee",
                        borderRight: colIndex !== columns.length - 1 ? "1px solid #ccc" : "none"
                      }}
                    >
                      {editTableData.editFieldStatus &&
                        editTableData.editFieldTableIndex === index &&
                        editTableData.editFieldTableRowIndex === rowIndex &&
                        editTableData.editFieldTableColumnKey === col &&
                        approvalStatus === "PENDING" ? (
                        <TextField
                          value={row[col] ?? ""}
                          size="small"
                          autoFocus
                          onChange={(e) =>
                            handleTableValueChange(filePage, index, rowIndex, col, e.target.value)
                          }
                          onBlur={() => setEditTableData({ ...editTableData, editFieldStatus: false })}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            cursor: "pointer",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            textAlign: "right"
                          }}
                          onClick={() =>
                            setEditTableData({
                              ...editTableData,
                              editFieldStatus: true,
                              editFieldTableIndex: index,
                              editFieldTableRowIndex: rowIndex,
                              editFieldTableColumnKey: col
                            })
                          }
                        >
                          {row[col] ?? "--"}
                        </Typography>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Collapse>
      </Box>
    );
  };

  const GroupItem = ({ group, index }) => {
    return (
      <Box>
        {/* ---- GROUP HEADER ---- */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
            {formatLabel(group?.group_name || group?.name)}
          </Typography>

          <IconButton
            sx={{ fontSize: '15px', fontWeight: 500 }}
            onClick={() => setGroupOpenIndex(groupOpenIndex === index ? null : index)}
          >
            {groupOpenIndex === index ? (
              <KeyboardArrowUp sx={{ color: '#0B51C5' }} />
            ) : (
              <KeyboardArrowDown sx={{ color: '#0B51C5' }} />
            )}
          </IconButton>
        </Box>

        {/* ---- GROUP COLLAPSE ---- */}
        <Collapse in={groupOpenIndex === index} timeout="auto" unmountOnExit sx={{ backgroundColor: '#FFFFFF' }}>
          <Box sx={{ marginLeft: '20px' }}>

            {/* ---- GROUP FIELDS ---- */}
            {Object.entries(group?.fields || {})
              .filter(([_, field]) => String(field?.value ?? '').trim() !== '')
              .map(([key, field], i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, minWidth: '200px', maxWidth: '300px', wordBreak: 'break-word' }}>
                    {field?.source_label}
                  </Typography>

                  <Box sx={{ flex: 1, textAlign: 'right' }}>
                    {editGroupData.editGroupFieldStatus &&
                      approvalStatus === 'PENDING' &&
                      editGroupData.editGroupIndex === index &&
                      editGroupData.editGroupFieldKey === key ? (
                      <TextField
                        value={field?.value ?? ''}
                        size="small"
                        fullWidth
                        autoFocus
                        onChange={e => handleGroupFieldValueChange(filePage, index, key, e.target.value)}
                        onBlur={() =>
                          setEditGroupData({
                            editGroupIndex: null,
                            editGroupFieldKey: null,
                            editGroupFieldStatus: false
                          })
                        }
                      />
                    ) : (
                      <Typography
                        sx={{ fontSize: '14px', fontWeight: 400, cursor: 'pointer', wordBreak: 'break-word' }}
                        onClick={() =>
                          setEditGroupData({
                            ...editGroupData,
                            editGroupFieldStatus: true,
                            editGroupIndex: index,
                            editGroupFieldKey: key
                          })
                        }
                      >
                        {String(field?.value ?? '').trim() || '--'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}

            {/* ---- GROUP TABLES ---- */}
            {(group?.tables || []).map((table, tableIndex) => {
              const columns = table?.rows?.length ? Object.keys(table.rows[0]).filter(key => key !== "_unmapped_columns") : [];
              return (
                <Box key={tableIndex}>
                  {/* TABLE HEADER */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      {formatLabel(table?.source_label)}
                    </Typography>

                    <IconButton
                      sx={{ fontSize: '14px', fontWeight: 500 }}
                      onClick={() =>
                        setGroupTableOpenIndex(groupTableOpenIndex === tableIndex ? null : tableIndex)
                      }
                    >
                      {groupTableOpenIndex === tableIndex ? (
                        <KeyboardArrowUp sx={{ color: '#0B51C5' }} />
                      ) : (
                        <KeyboardArrowDown sx={{ color: '#0B51C5' }} />
                      )}
                    </IconButton>
                  </Box>

                  {/* TABLE COLLAPSE */}
                  <Collapse
                    in={groupTableOpenIndex === tableIndex}
                    timeout="auto"
                    unmountOnExit
                    sx={{ backgroundColor: '#FFFFFF', p: 2 }}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontFamily: 'Arial, sans-serif',
                        border: '1px solid #ccc'
                      }}
                    >
                      <thead>
                        <tr>
                          {columns.map((col, colIndex) => (
                            <th
                              key={colIndex}
                              style={{
                                textAlign: 'left',
                                fontSize: '14px',
                                fontWeight: 500,
                                padding: '8px',
                                borderBottom: '1px solid #ccc',
                                borderRight: colIndex !== columns.length - 1 ? '1px solid #ccc' : 'none',
                                textTransform: 'capitalize'
                              }}
                            >
                              {col.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {table.rows?.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {columns.map((col, colIndex) => (
                              <td
                                key={colIndex}
                                style={{
                                  textAlign: colIndex === columns.length - 1 ? 'right' : 'left',
                                  fontSize: '14px',
                                  padding: '8px',
                                  borderTop: '1px solid #eee',
                                  borderRight:
                                    colIndex !== columns.length - 1 ? '1px solid #ccc' : 'none'
                                }}
                              >
                                {editGroupData.editGroupTableFieldStatus &&
                                  editGroupData.editGroupIndex === index &&
                                  editGroupData.editGroupTableIndex === tableIndex &&
                                  editGroupData.editGroupTableRowIndex === rowIndex &&
                                  editGroupData.editGroupTableColumnKey === col &&
                                  approvalStatus === 'PENDING' ? (
                                  <TextField
                                    value={row[col] ?? ''}
                                    size="small"
                                    autoFocus
                                    onChange={e =>
                                      handleGroupTableFieldValueChange(
                                        filePage,
                                        index,
                                        tableIndex,
                                        rowIndex,
                                        col,
                                        e.target.value
                                      )
                                    }
                                    onBlur={() =>
                                      setEditGroupData({ ...editGroupData, editGroupTableFieldStatus: false })
                                    }
                                  />
                                ) : (
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 400,
                                      textAlign: 'right',
                                      cursor: 'pointer',
                                      wordBreak: 'break-word',
                                      whiteSpace: 'normal'
                                    }}
                                    onClick={() =>
                                      setEditGroupData({
                                        ...editGroupData,
                                        editGroupTableFieldStatus: true,
                                        editGroupIndex: index,
                                        editGroupTableIndex: tableIndex,
                                        editGroupTableRowIndex: rowIndex,
                                        editGroupTableColumnKey: col
                                      })
                                    }
                                  >
                                    {row[col] ?? '--'}
                                  </Typography>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const filteredFields = useMemo(
    () =>
      Object.entries(extractionResponse?.files?.[filePage]?.mapped_fields ?? {}).filter(
        ([_, field]) => String(field?.value ?? '').trim() !== '' && !field?.data_absent && field?.source_label != null
      ),
    [extractionResponse, filePage]
  );

  const hasTableRows = useMemo(
    () => (extractionResponse?.files?.[filePage]?.tables ?? []).some(table => table?.rows?.length > 0),
    [extractionResponse, filePage]
  );

  const handleLaunch = async () => {

    const third_party_token = await fetchThirdPartyToken();
    const token_provider = await fetchThirdPartyTokenProvider()

    const params = {
      token_provider: token_provider,
      third_party_token: third_party_token
    };

    if (window.toggleChatbot) {
      window.toggleChatbot(params);
    } else {
      console.warn("Chatbot script not loaded yet");
    }

  };

  return (
    <Box sx={styles.mainContainer}>
      {(loading || dataLoading) && <LoadBar />}
      <Box sx={styles.cardContainer}>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr",
              md: "minmax(320px, 45%) minmax(320px, 55%)",
              lg: "minmax(380px, 48%) minmax(380px, 52%)",
              xl: "minmax(420px, 48%) minmax(420px, 52%)",
            },
            gap: 0.2,
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              backgroundColor: '#FFFFFF',
              borderRadius: "12px",
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px',
                paddingBottom: extractionResponse?.files?.length > 1 ? 0 : '20px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => navigate('/all-documents')}>
                  <ArrowBack sx={{ color: '#0B51C5' }} />
                </IconButton>
                <Tooltip title={isLongName ? getDecodedName(selectedFileName) : ''} placement="top">
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '250px',
                      cursor: isLongName ? 'pointer' : 'default',
                    }}
                  >
                    {getDecodedName(selectedFileName)}
                  </Typography>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton disabled={loading || dataLoading} onClick={e => setDownloadAnchorEl(e.currentTarget)}>
                  <Download sx={{ color: '#0B51C5' }} />
                </IconButton>
                {approvalStatus === 'PENDING' ? (
                  <Button disabled={loading || dataLoading} sx={{ ...styles.approveBtn, marginLeft: '5px' }} onClick={handleApprove}>
                    <Typography>Approve</Typography>
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '5px' }}>
                    <ReactSVG
                      src={IconSvg.check3Icon}
                      beforeInjection={svg => svg.setAttribute('style', 'width: 24px; height: 24px; display: block;')}
                    />
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#17803D', marginLeft: '3px' }}>Approved</Typography>
                  </Box>
                )}
              </Box>

            </Box>

            {extractionResponse?.files?.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '5px', marginBottom: '10px' }}>
                <Pagination
                  count={extractionResponse?.files?.length}
                  page={filePage + 1}
                  onChange={(e, value) => setFilePage(value - 1)}
                  shape="circular"
                  sx={styles.navigationBtn}
                />
              </Box>
            )}

            {!dataLoading && <Divider />}

            {extractionResponse?.files?.some(
              file => file.tables?.length > 0 || file.groups?.length > 0 || Object.keys(file.mapped_fields || {}).length > 0
            ) ? (
              <Box>
                <Box sx={{ marginTop: '5px', padding: filteredFields.length > 0 ? '20px' : 0 }}>
                  {filteredFields.map(([key, field], i) => (
                    <FieldItem key={i} field={field} keyName={key} index={i} />
                  ))}
                </Box>

                {filteredFields.length > 0 && hasTableRows && <Divider />}

                <Box sx={{ padding: extractionResponse?.files?.[filePage]?.tables?.length > 0 ? '20px' : 0 }}>
                  {extractionResponse?.files?.[filePage]?.tables?.map((table, index) => (
                    <TableItem key={index} table={table} index={index} />
                  ))}
                </Box>

                <GroupDivider />

                <Box sx={{ padding: extractionResponse?.files?.[filePage]?.groups?.length > 0 ? '20px' : 0 }}>
                  {extractionResponse?.files?.[filePage]?.groups?.map((group, index) => (
                    <GroupItem key={index} group={group} index={index} />
                  ))}
                </Box>

              </Box>
            ) : dataLoading ? (
              <Box >
                <Divider />
                <Typography sx={{ marginTop: '120px', textAlign: 'center' }}>
                  Loading...
                </Typography>
              </Box>
            ) : (
              <Box >
                <Divider />
                <Typography sx={{ marginTop: '120px', textAlign: 'center' }}>
                  No Data Available
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              borderRadius: "12px",
              display: 'flex',
              alignItems: 'flex-start'
            }}
          >
            <Box
              sx={{
                width: '10%',
                height: '100%',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'end',
                justifyContent: 'center',
                marginRight: '10px',
              }}
            >
              <Box
                sx={{
                  padding: '5px',
                  backgroundColor: '#0B51C5',
                  border: 'solid 1px #FFFFFF',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}
                onClick={() => setPreviewType('file_preview')}
              >
                <ReactSVG
                  src={IconSvg.filesIcon}
                  beforeInjection={svg => {
                    svg.setAttribute('style', 'width: 24px; height: 24px; display: block;');
                    svg.querySelectorAll('path').forEach(path => path.setAttribute('stroke', '#FFFFFF'));
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', margin: '5px 0px' }}>
                <IconButton onClick={() => changePage(pageNumber - 1)} disabled={pageNumber <= 1 || dataLoading || isOcrProcessing}>
                  <KeyboardArrowUp style={{ height: '18px', width: '18px' }} />
                </IconButton>
                <Typography sx={styles.paraText}>{pageNumber}</Typography>
                <IconButton onClick={() => changePage(pageNumber + 1)} disabled={pageNumber >= totalPages || dataLoading || isOcrProcessing}>
                  <KeyboardArrowDown style={{ height: '18px', width: '18px' }} />
                </IconButton>
              </Box>

              <Box
                sx={{
                  padding: '5px',
                  backgroundColor: '#0B51C5',
                  border: 'solid 1px #FFFFFF',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}
                onClick={() => setPreviewType('extraction')}
              >
                <ReactSVG
                  src={IconSvg.jsonViewIcon}
                  beforeInjection={svg => {
                    svg.setAttribute('style', 'width: 24px; height: 24px; display: block;');
                    svg.querySelectorAll('path').forEach(path => path.setAttribute('stroke', '#FFFFFF'));
                  }}
                />
              </Box>
            </Box>

            <Box
              ref={containerRef}
              sx={{
                width: '90%',
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: '#FFFFFF',
                borderRadius: '15px',
                position: 'relative',
              }}
            >
              {dataLoading ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : previewType === 'file_preview' ? (
                <Box sx={{ flex: 1, p: 1, display: 'flex', height: '100%', width: '100%' }}>
                  <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>

                    {
                      fileType !== 'docx' && fileType !== 'xlsx' && fileType !== "pdf" &&
                        fileType !== "jpeg" && fileType !== 'png' ?
                        (<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography>No Preview Available</Typography>
                        </Box>)
                        :
                        (<Box sx={{ position: 'relative', border: '1px solid', borderColor: '#d1d5db', borderRadius: '4px' }}>

                          {(fileType === 'pdf') &&
                            <Box ref={containerRef} className="pdf-container">
                              <canvas ref={pdfCanvasRef} className="pdf-canvas" />
                              <canvas
                                ref={canvasRef}
                                style={{ position: 'absolute', top: 0, left: 0, cursor: 'default', pointerEvents: 'auto' }}
                                className="annotation-canvas"
                              />
                            </Box>
                          }

                          {fileType === 'docx' &&
                            <Box
                              className="docx-container"
                              style={{
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                width: '100%',
                                paddingTop: '20px'
                              }}
                            >
                              <div ref={relativeRef} style={{ position: 'relative', width: 'fit-content', height: 'fit-content' }}>
                                <div ref={contentRef} />
                                <canvas
                                  ref={canvasRef}
                                  style={{
                                    pointerEvents: 'auto',
                                    cursor: 'default',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 10,
                                  }}
                                  className="annotation-canvas"
                                />
                              </div>

                            </Box>
                          }

                          {fileType === 'xlsx' &&
                            <Box
                              ref={containerRef}
                              className="pdf-container"
                              style={{
                                cursor: 'pointer'
                              }}
                            >
                              {renderContent()}
                              < canvas
                                ref={canvasRef}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  pointerEvents: 'none',
                                  zIndex: 10
                                }}
                              />
                            </Box>
                          }

                          {(fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png') && (
                            <Box
                              className="image-container"
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '100vh',
                              }}
                            >
                              <img
                                src={fileDoc?.uri}
                                alt="preview"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                }}
                              />
                            </Box>
                          )}

                        </Box>)
                    }

                  </Stack>
                </Box>
              ) : (
                <Box sx={{ padding: '12px', width: '100%', height: '100%' }}>
                  <JSONTree data={extractionResponseData} theme={lightTheme} invertTheme={false} />
                </Box>
              )}
            </Box>

          </Box>
        </Box>

        {(chatbotData.chatbotStatus && chatbotData.chatBotType === "advanced_bot" && chatbotData.chatbotUrl) && <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000 }}>
          <Button
            id="chatBotButton"
            variant="contained"
            sx={styles.chatbotBtn}
            onClick={handleLaunch}
          >
            💬
          </Button>
        </Box>}

        <Popover
          open={Boolean(downloadAnchorEl)}
          anchorEl={downloadAnchorEl}
          onClose={() => setDownloadAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ borderRadius: '5px' }}
        >
          <Box sx={{ p: 1, minWidth: 200 }}>
            <MenuItem onClick={handleDownloadFile}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReactSVG
                  src={IconSvg.filesIcon}
                  beforeInjection={svg => {
                    svg.setAttribute('style', 'width: 18px; height: 18px; display: block;');
                    svg.querySelectorAll('path').forEach(path => path.setAttribute('stroke', '#000000'));
                  }}
                />
                <Typography sx={{ fontSize: '14px', fontWeight: 400, marginLeft: '10px' }}>Original File</Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={handleDownloadJSON}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReactSVG
                  src={IconSvg.jsonViewIcon}
                  beforeInjection={svg => {
                    svg.setAttribute('style', 'width: 18px; height: 18px; display: block;');
                    svg.querySelectorAll('path').forEach(path => path.setAttribute('stroke', '#000000'));
                  }}
                />
                <Typography sx={{ fontSize: '14px', fontWeight: 400, marginLeft: '10px' }}>Download JSON</Typography>
              </Box>
            </MenuItem>
          </Box>
        </Popover>
      </Box>
    </Box >
  );
};

export default ViewDocument;